import React, { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import api from "../../services/api"
import styles from "./outboundCampaignDetails.module.css"
import Table from "../../components/Table"
import moment from "moment"
import { toast } from "react-toastify"
import Loading from "../layouts/Loading"
import BackButton from "../../components/BackButton"
import { getPropertyForItemInArrayById } from "../../utils/utils"
import InfoTooltip from "../../components/InfoTooltip"
import { WhatsAppTemplatePreview } from "../../components/WhatsAppTemplateCard"
import Button from "../../components/Form/Button"
import Input from "../../components/Form/Input"
import Select from "../../components/Form/Select"
import {
    MdAccessTime,
    MdCalendarToday,
    MdDoneAll,
    MdErrorOutline,
    MdFilterAltOff,
    MdGroups,
    MdInbox,
    MdMarkEmailRead,
    MdOutlineOpenInNew,
    MdOutlineRefresh,
    MdReply,
    MdSend,
    MdPause,
    MdPlayArrow,
    MdReplay,
    MdSchedule,
    MdPersonOutline,
} from "react-icons/md"
import PdfExportButton from "../Dashboard/PdfExportButton"
import { useAuth } from "../../contexts/authContext"
import CampaignScheduleModal from "../OutboundCampaignList/CampaignScheduleModal"
import CampaignRetryModal from "../OutboundCampaignList/CampaignRetryModal"

const optionsStatus = [
    { id: "pending", value: "Pendente" },
    { id: "processing", value: "Preparando" },
    { id: "queued", value: "Na fila" },
    { id: "submitted", value: "Processada" },
    { id: "sent", value: "Enviada" },
    { id: "delivered", value: "Entregue" },
    { id: "read", value: "Lida" },
    { id: "replied", value: "Respondida" },
    { id: "failed", value: "Falhou" },
    { id: "canceled", value: "Cancelada" },
]

const campaignStatus = {
    draft: "Rascunho",
    scheduled: "Agendada",
    sending: "Enviando",
    finished: "Finalizada",
    paused: "Pausada",
    canceled: "Cancelada",
}

const campaignStatusTone = {
    draft: "neutral",
    scheduled: "info",
    sending: "warning",
    finished: "success",
    paused: "warning",
    canceled: "danger",
}

const metricIcons = {
    total: MdGroups,
    sent: MdSend,
    delivered: MdDoneAll,
    read: MdMarkEmailRead,
    replied: MdReply,
    failed: MdErrorOutline,
}

const retryOutcomeLabels = {
    scheduled: "Agendada",
    processing: "Processando",
    queued: "Na fila",
    submitted: "Processada",
    sent: "Enviada",
    delivered: "Entregue",
    read: "Lida",
    replied: "Respondida",
    failed: "Falhou",
}

const getRetryDescription = item => {
    const attempt = item?.retry_history?.at(-1)
    if (!attempt) return "Envio original"
    return [
        `${attempt.attempt_number || item.retry_count}ª nova tentativa`,
        attempt.requested_by_name,
        retryOutcomeLabels[attempt.outcome] || attempt.outcome,
    ].filter(Boolean).join(" · ")
}

const getCampaignMessageRows = message => {
    const content = message?.preview_payload?.content || message?.template_content || {}
    const rows = []
    const header = content?.header || {}

    if (header.text) {
        rows.push({ value: ["Cabeçalho", header.text] })
    } else if (header.format && header.format !== "TEXT") {
        const mediaDescription = [
            header.format,
            header.filename,
            header.location?.address,
        ].filter(Boolean).join(" • ")
        rows.push({ value: ["Cabeçalho", mediaDescription || header.format] })
    }

    if (content?.body?.text) {
        rows.push({ value: ["Corpo", content.body.text] })
    } else if (typeof content?.text === "string") {
        rows.push({ value: ["Corpo", content.text] })
    }

    if (content?.footer?.text) {
        rows.push({ value: ["Rodapé", content.footer.text] })
    }

    if (Array.isArray(content?.buttons) && content.buttons.length > 0) {
        content.buttons.forEach((button, index) => {
            const buttonContent = [
                button?.text,
                button?.url,
                button?.phone_number,
            ].filter(Boolean).join(" — ")
            rows.push({
                value: [`Botão ${index + 1}`, buttonContent || button?.type || "-"],
            })
        })
    }

    if (!rows.length && Array.isArray(content)) {
        content.forEach(component => {
            const type = component?.type || "Componente"
            const text = component?.text || component?.format || "-"
            rows.push({ value: [type, text] })
        })
    }

    return rows.length
        ? rows
        : [{ value: ["Conteúdo", "O conteúdo textual deste template não está disponível."] }]
}

const OutboundCampaignDetails = () => {
    const { campaign_id, account_id } = useParams()

    const [campaign, setCampaign] = useState(null)
    const [score, setScore] = useState(null)
    const [cards, setCards] = useState([])
    const [highlights, setHighlights] = useState([])
    const [failureGroups, setFailureGroups] = useState([])
    const [retryEligible, setRetryEligible] = useState(null)
    const [retrySummary, setRetrySummary] = useState(null)
    const [message, setMessage] = useState({})
    const [recipients, setRecipients] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [isLoading, setIsLoading] = useState(false)
    const [isFetchingMore, setIsFetchingMore] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [lastUpdated, setLastUpdated] = useState(null)
    const [search, setSearch] = useState("")
    const [appliedSearch, setAppliedSearch] = useState("")
    const [statusFilter, setStatusFilter] = useState("")
    const [loadError, setLoadError] = useState("")
    const [actionLoading, setActionLoading] = useState(false)
    const [scheduleModal, setScheduleModal] = useState(false)
    const [retryConfirmOpen, setRetryConfirmOpen] = useState(false)
    const { account, user } = useAuth()

    useEffect(() => {
        const timeout = setTimeout(() => setAppliedSearch(search.trim()), 350)
        return () => clearTimeout(timeout)
    }, [search])

    useEffect(() => {
        fetchData(true)
    }, [campaign_id, appliedSearch, statusFilter])

    const handleScroll = () => {
        if (!isFetchingMore && !isLoading && recipients.length < total) {
            fetchData(false)
        }
    }

    const headers = [
        "#",
        "Contato",
        "Telefone",
        "Status",
        "Tentativa",
        "Enviado em",
        "Entregue",
        "Lido",
        "Respondido",
        "Falha",
        "Conversa"
    ]

    const getDeliveryErrorMessage = (item) => {
        if (!item?.error_code && !item?.error_message) {
            return ""
        }

        return [
            item.error_code ? `Erro ${item.error_code}` : "",
            item.error_message || ""
        ].filter(Boolean).join(": ")
    }

    const getFailureCell = (item) => {
        if (item.status !== "failed") {
            return "-"
        }

        const errorMessage = getDeliveryErrorMessage(item)

        if (!errorMessage) {
            return <span className={styles.statusFailed}>Falhou</span>
        }

        return (
            <span className={styles.errorCell}>
                <span className={styles.statusFailed}>{item.error_code ? `Erro ${item.error_code}` : "Falhou"}</span>
                <InfoTooltip text={errorMessage} preferredPosition="top" />
            </span>
        )
    }

    const getConversationCell = (item) => {
        if (!item?.conversation_id) {
            return "-"
        }

        return (
            <a
                className={styles.conversationLink}
                href={`/${account_id}/chat/${item.conversation_id}`}
                target="_blank"
                rel="noreferrer"
                aria-label={`Abrir conversa de ${item.contact_name || item.contact_phone || "contato"}`}
            >
                <MdOutlineOpenInNew />
                Abrir
            </a>
        )
    }

    const fetchData = async (reset = false, options = {}) => {
        if (!reset && (isLoading || isFetchingMore || isRefreshing)) return

        try {
            if (reset && !options.silent) {
                setIsLoading(true)
            } else if (reset) {
                setIsRefreshing(true)
            } else if (!reset) {
                setIsFetchingMore(true)
            }
            setLoadError("")

            const currentPage = reset ? 1 : page
            const params = new URLSearchParams({ page: `${currentPage}` })
            if (appliedSearch) params.set("search", appliedSearch)
            if (statusFilter) params.set("status", statusFilter)

            const response = await api.get(
                `/outbound-campaigns/${campaign_id}?${params.toString()}`
            )

            const data = response.data

            if (reset) {
                setCampaign(data.campaign)
                setScore(data.score)
                setCards(data.cards || [])
                setHighlights(data.highlights || [])
                setFailureGroups(data.failureGroups || [])
                setRetryEligible(data.retryEligible === undefined ? null : Number(data.retryEligible || 0))
                setRetrySummary(data.retrySummary || null)
                setMessage(data.message || {})
                setRecipients(previous => options.silent && !appliedSearch && !statusFilter && previous.length > (data.recipients || []).length
                    ? [...(data.recipients || []), ...previous.slice((data.recipients || []).length)]
                    : (data.recipients || []))
                setTotal(data.totalRecipients || 0)
                if (!options.silent || appliedSearch || statusFilter) setPage(2)
            } else {
                setRecipients(prev => [...prev, ...(data.recipients || [])])
                setPage(prev => prev + 1)
            }
            setLastUpdated(new Date())

        } catch (err) {
            console.error("Erro ao buscar campanha:", err)
            setLoadError(err?.response?.data?.message || "Não foi possível atualizar os detalhes da campanha.")
        } finally {
            if (!options.silent) setIsLoading(false)
            setIsRefreshing(false)
            setIsFetchingMore(false)
        }
    }

    const getAllRecipientsForPdf = async () => {
        const pageSize = 500
        let currentPage = 1
        let totalRecipients = 0
        let allRecipients = []
        let reportData = null

        do {
            const response = await api.get(
                `/outbound-campaigns/${campaign_id}?page=${currentPage}&pageSize=${pageSize}`
            )
            reportData = reportData || response.data
            totalRecipients = response.data?.totalRecipients || 0
            allRecipients = [...allRecipients, ...(response.data?.recipients || [])]
            currentPage += 1
        } while (allRecipients.length < totalRecipients)

        return { ...reportData, recipients: allRecipients }
    }

    const buildCampaignPdf = async () => {
        const data = await getAllRecipientsForPdf()
        const reportCampaign = data.campaign || campaign
        const reportMessage = data.message || message

        return {
            title: `Campanha - ${reportCampaign?.name || "Detalhes"}`,
            accountName: account?.company?.fantasy_name || account?.name || "Conta",
            generatedBy: user?.name,
            filters: [
                { label: "Caixa de entrada", value: reportCampaign?.inbox?.name || "Caixa removida" },
                { label: "Criada por", value: reportCampaign?.creator?.name || "Criador não identificado" },
                { label: "Status", value: campaignStatus[reportCampaign?.status] || reportCampaign?.status },
                { label: "Agendamento", value: reportCampaign?.scheduled_at ? moment(reportCampaign.scheduled_at).format("DD/MM/YYYY HH:mm") : "-" },
                { label: "Template", value: reportMessage?.template_name || "-" },
                { label: "Idioma", value: reportMessage?.template_language || "-" },
                { label: "Pontuação", value: data.score ? `${data.score.value}/100 - ${data.score.label}` : "-" },
            ],
            data: {
                cards: data.cards || cards,
                tables: [
                    {
                        title: "Mensagem enviada",
                        headers: ["Componente", "Conteúdo"],
                        values: getCampaignMessageRows(reportMessage),
                    },
                    {
                        title: "Destinatários",
                        headers: ["Contato", "Telefone", "Status", "Tentativa", "Solicitada em", "Enviado em", "Entregue em", "Lido em", "Respondido em", "Falha"],
                        values: (data.recipients || []).map(item => ({
                            value: [
                                item.contact_name || "-",
                                item.contact_phone || "-",
                                getPropertyForItemInArrayById(item.status, optionsStatus, "id", "value"),
                                getRetryDescription(item),
                                item.last_retry_at ? moment(item.last_retry_at).format("DD/MM/YYYY HH:mm") : "-",
                                item.sent_at ? moment(item.sent_at).format("DD/MM/YYYY HH:mm") : "-",
                                item.delivered_at ? moment(item.delivered_at).format("DD/MM/YYYY HH:mm") : "-",
                                item.read_at ? moment(item.read_at).format("DD/MM/YYYY HH:mm") : "-",
                                item.replied_at ? moment(item.replied_at).format("DD/MM/YYYY HH:mm") : "-",
                                getDeliveryErrorMessage(item) || "-",
                            ],
                        })),
                    },
                ],
            },
        }
    }

    const getTableValues = () => {
        return recipients.map(item => ({
            id: item._id,
            value: [
                item.contact_name || "-",
                item.contact_phone || "-",
                <span className={`${styles.deliveryStatus} ${styles[`deliveryStatus_${item.status}`]}`}>
                    {getPropertyForItemInArrayById(item.status, optionsStatus, "id", "value")}
                </span>,
                <div className={styles.retryInfo}>
                    <strong>{getRetryDescription(item)}</strong>
                    {item.last_retry_at && <small>Solicitada em {moment(item.last_retry_at).format("DD/MM/YYYY HH:mm")}</small>}
                </div>,
                item.sent_at
                    ? moment(item.sent_at).format("DD/MM/YYYY HH:mm")
                    : "-",
                item.delivered_at ? moment(item.delivered_at).format("DD/MM/YYYY HH:mm") : "-",
                item.read_at ? moment(item.read_at).format("DD/MM/YYYY HH:mm") : "-",
                item.replied_at ? moment(item.replied_at).format("DD/MM/YYYY HH:mm") : "-",
                getFailureCell(item),
                getConversationCell(item)
            ]
        }))
    }

    const cardsById = useMemo(() => Object.fromEntries(cards.map(card => [card.id, card])), [cards])
    const funnelMetrics = ["total", "sent", "delivered", "read", "replied", "failed"]
        .map(id => cardsById[id])
        .filter(Boolean)
    const performanceRates = ["delivery_rate", "read_rate", "reply_rate", "failure_rate"]
        .map(id => cardsById[id])
        .filter(Boolean)
    const totalCampaignRecipients = Number(cardsById.total?.value || campaign?.total_contacts || 0)
    const processedRecipients = Number(cardsById.sent?.value || 0)
        + Number(cardsById.failed?.value || 0)
        + Number(cardsById.canceled?.value || 0)
    const campaignProgress = totalCampaignRecipients > 0
        ? Math.min(100, Math.round((processedRecipients / totalCampaignRecipients) * 100))
        : 0
    const hasActiveFilters = Boolean(search || statusFilter)
    const failedCount = Number(cardsById.failed?.value || 0)
    const effectiveRetryEligible = retryEligible === null ? failedCount : retryEligible
    const actualFailedCount = Number(retrySummary?.failed ?? failedCount)
    const retryState = retrySummary?.state || (effectiveRetryEligible > 0 ? "available" : failedCount > 0 ? "limit_reached" : "none")
    const campaignTone = campaignStatusTone[campaign?.status] || "neutral"
    const hasMessage = Boolean(message?._id || message?.template_name || message?.template_content || message?.preview_payload?.content)

    const clearFilters = () => {
        setSearch("")
        setAppliedSearch("")
        setStatusFilter("")
    }

    const handleOpenRetryConfirmation = () => {
        if (campaign?.status !== "finished") {
            toast.info("O reenvio ficará disponível assim que o processamento da campanha for finalizado.")
            return
        }
        if (retryState === "metrics_mismatch") {
            handleRetryFailures()
            return
        }
        if (retryState === "limit_reached") {
            toast.info("Todas as mensagens com falha já atingiram o limite de três novas tentativas.")
            return
        }
        if (retryState !== "available") {
            toast.info("Não existem mensagens com falha para reenviar.")
            return
        }
        setRetryConfirmOpen(true)
    }

    const handleCampaignAction = async (action, extra = {}) => {
        setActionLoading(true)
        try {
            const response = await api.post("/outbound-campaigns/bulk-action", { action, selection: { ids: [campaign_id] }, ...extra })
            toast.success(response.data?.message || "Campanha atualizada.")
            setScheduleModal(false)
            await fetchData(true, { silent: true })
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível atualizar a campanha.")
            throw err
        } finally {
            setActionLoading(false)
        }
    }

    const handleRetryFailures = async scheduled_at => {
        setActionLoading(true)
        try {
            const response = await api.post(`/outbound-campaigns/${campaign_id}/retry-failures`, { scheduled_at })
            toast.success(response.data?.message)
            setRetryConfirmOpen(false)
            await fetchData(true, { silent: true })
        } catch (err) {
            const responseCode = err?.response?.data?.code
            if (responseCode === "campaign_failure_metrics_reconciled") {
                toast.warning(err.response.data.message)
                setRetryConfirmOpen(false)
                await fetchData(true, { silent: true })
            } else {
                toast.error(err?.response?.data?.message || "Não foi possível reenviar as mensagens com falha.")
            }
        } finally {
            setActionLoading(false)
        }
    }

    if (isLoading && !campaign) return <Loading fullPage />

    return (
        <div className={styles.container}>
            <div className={styles.topBar}>
                <BackButton fallback={`/${account_id}/integrations/chat/outbound-campaign/list`} />
                <div className={styles.reportActions}>
                    {lastUpdated && (
                        <span className={styles.updatedAt}>
                            <i className={isRefreshing ? styles.refreshingDot : ""} />
                            Atualizado às {moment(lastUpdated).format("HH:mm:ss")}
                        </span>
                    )}
                    <Button
                        type="button"
                        variant="secondary"
                        fullWidth={false}
                        onClick={() => fetchData(true, { silent: true })}
                        disabled={isRefreshing}
                    >
                        <MdOutlineRefresh className={isRefreshing ? styles.spinning : ""} />
                        {isRefreshing ? "Atualizando" : "Atualizar"}
                    </Button>
                    <PdfExportButton
                        buildReport={buildCampaignPdf}
                        disabled={isLoading}
                    />
                </div>
            </div>
            {loadError && <div className={styles.loadError}><MdErrorOutline /> {loadError}</div>}

            <div className={styles.campaignActions}>
                {["scheduled", "sending"].includes(campaign?.status) && <Button variant="secondary" fullWidth={false} disabled={actionLoading} onClick={() => handleCampaignAction("pause")}><MdPause /> Pausar</Button>}
                {campaign?.status === "paused" && <Button variant="secondary" fullWidth={false} disabled={actionLoading} onClick={() => handleCampaignAction("resume")}><MdPlayArrow /> Retomar</Button>}
                {["draft", "scheduled", "paused"].includes(campaign?.status) && <Button variant="secondary" fullWidth={false} disabled={actionLoading} onClick={() => setScheduleModal(true)}><MdSchedule /> Reagendar</Button>}
                {(failedCount > 0 || actualFailedCount > 0) && <Button variant="secondary" fullWidth={false} disabled={actionLoading} title="Reenviar somente as mensagens elegíveis" onClick={handleOpenRetryConfirmation}><MdReplay /> Reenviar mensagens com falha</Button>}
            </div>

            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <div className={styles.titleRow}>
                        <div>
                            <span className={styles.eyebrow}>Detalhes da campanha</span>
                            <h1 className={styles.title}>{campaign?.name || "Campanha"}</h1>
                        </div>
                        <span className={`${styles.campaignStatus} ${styles[`campaignStatus_${campaignTone}`]}`}>
                            {campaignStatus[campaign?.status] || campaign?.status}
                        </span>
                    </div>
                    {campaign?.description && <p className={styles.description}>{campaign.description}</p>}
                    <div className={styles.metaInfo}>
                        <span><MdInbox /> {campaign?.inbox?.name || "Caixa removida"}</span>
                        <span><MdPersonOutline /> Criada por {campaign?.creator?.name || "usuário não identificado"}</span>
                        <span><MdGroups /> {totalCampaignRecipients} destinatário{totalCampaignRecipients === 1 ? "" : "s"}</span>
                        {campaign?.scheduled_at && <span><MdCalendarToday /> {moment(campaign.scheduled_at).format("DD/MM/YYYY HH:mm")}</span>}
                        {campaign?.started_at && <span><MdAccessTime /> Iniciada em {moment(campaign.started_at).format("DD/MM/YYYY HH:mm")}</span>}
                    </div>
                    <div className={styles.campaignProgressHeader}>
                        <span>{campaign?.status === "finished" ? "Processamento concluído" : "Progresso do processamento"}</span>
                        <strong>{campaignProgress}%</strong>
                    </div>
                    <div
                        className={styles.campaignProgress}
                        role="progressbar"
                        aria-label="Progresso do processamento da campanha"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={campaignProgress}
                    >
                        <div style={{ width: `${campaignProgress}%` }} />
                    </div>
                </div>

                {score && (
                    <div className={styles.scoreBox}>
                        <div className={styles.scoreTitle}>
                            Performance
                            <InfoTooltip
                                text="Pontuação calculada a partir das taxas de entrega, leitura, resposta e falha."
                                preferredPosition="top"
                            />
                        </div>
                        <div className={styles.scoreMain}>
                            <div className={styles.scoreValue}>{score.value}<span>/100</span></div>
                            <div className={`${styles.scoreBadge} ${styles[`scoreBadge_${score.type}`]}`}>{score.label}</div>
                        </div>
                        <div
                            className={styles.scoreProgress}
                            role="progressbar"
                            aria-label="Pontuação de performance da campanha"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            aria-valuenow={score.value}
                        >
                            <div className={`${styles.scoreBar} ${styles[`scoreBar_${score.type}`]}`} style={{ width: `${score.value}%` }} />
                        </div>
                    </div>
                )}
            </section>

            <section className={styles.metricsSection}>
                <div className={styles.sectionHeading}>
                    <div>
                        <span className={styles.eyebrow}>Funil de envio</span>
                        <h2>Resultados da campanha</h2>
                    </div>
                </div>
                <div className={styles.metricsGrid}>
                    {funnelMetrics.map(metric => {
                        const Icon = metricIcons[metric.id] || MdGroups
                        return (
                            <div key={metric.id} className={`${styles.metricCard} ${metric.id === "failed" ? styles.metricCardDanger : ""}`}>
                                <div className={styles.metricIcon}><Icon /></div>
                                <div className={styles.metricContent}>
                                    <span>{metric.name}</span>
                                    <strong>{metric.value}</strong>
                                </div>
                                {metric.tooltip && <InfoTooltip text={metric.tooltip} preferredPosition="top" />}
                            </div>
                        )
                    })}
                </div>
            </section>

            <div className={styles.contentGrid}>
                <section className={styles.insightsSection}>
                    <div className={styles.sectionHeading}>
                        <div>
                            <span className={styles.eyebrow}>Análise</span>
                            <h2>Desempenho e alertas</h2>
                        </div>
                    </div>
                    <div className={styles.ratesGrid}>
                        {performanceRates.map(rate => (
                            <div key={rate.id} className={styles.rateItem}>
                                <span>{rate.name}</span>
                                <strong className={styles[`rate_${rate.type || "default"}`]}>{rate.value}</strong>
                            </div>
                        ))}
                    </div>
                    {campaign?.status === "paused" && campaign?.pause_reason && (
                        <div className={`${styles.insight} ${styles.insight_warning}`}>
                            <MdErrorOutline />
                            <div><strong>Campanha pausada</strong><span>{campaign.pause_reason}</span></div>
                        </div>
                    )}
                    <div className={styles.insightsList}>
                        {highlights.map(item => (
                            <div key={item.id} className={`${styles.insight} ${styles[`insight_${item.type}`]}`}>
                                {item.type === "danger" ? <MdErrorOutline /> : <MdDoneAll />}
                                <div><strong>{item.title}</strong><span>{item.description}</span></div>
                                {item.tooltip && <InfoTooltip text={item.tooltip} preferredPosition="top" />}
                            </div>
                        ))}
                        {!highlights.length && campaign?.status !== "paused" && (
                            <div className={`${styles.insight} ${styles.insight_neutral}`}>
                                <MdDoneAll />
                                <div><strong>Nenhum alerta no momento</strong><span>Os indicadores da campanha estão dentro do esperado.</span></div>
                            </div>
                        )}
                    </div>
                    {failureGroups.length > 0 && (
                        <div className={styles.failureGroups}>
                            <strong>Principais causas de falha</strong>
                            {failureGroups.map((group, index) => (
                                <div key={`${group.code}-${index}`}>
                                    <span>{group.message}</span>
                                    <small>{group.count} ocorrência{group.count === 1 ? "" : "s"} · {group.eligible} {group.eligible === 1 ? "elegível" : "elegíveis"} para nova tentativa</small>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {hasMessage && (
                    <section className={styles.messageSection}>
                        <div className={styles.messageHeader}>
                            <div>
                                <span className={styles.eyebrow}>Conteúdo</span>
                                <h2>Mensagem enviada</h2>
                            </div>
                            <InfoTooltip text="Esta é a mensagem padrão enviada para todos os destinatários da campanha." />
                        </div>
                        <div className={styles.templateMeta}>
                            <strong>{message.template_name || "Template"}</strong>
                            <span>{message.template_language || "Idioma não informado"}</span>
                        </div>
                        <WhatsAppTemplatePreview
                            className={styles.messagePreview}
                            template={{ content: message.preview_payload?.content || message.template_content }}
                        />
                    </section>
                )}
            </div>

            <section className={styles.historySection}>
                <div className={styles.sectionHeading}>
                    <div><span className={styles.eyebrow}>Auditoria</span><h2>Histórico da campanha</h2></div>
                </div>
                <div className={styles.timeline}>
                    {[...(campaign?.history || [])].reverse().map((event, index) => (
                        <div key={`${event.event}-${event.created_at}-${index}`} className={styles.timelineItem}>
                            <i />
                            <div><strong>{event.label}</strong><span>{event.user_name || "Sistema"} · {moment(event.created_at).format("DD/MM/YYYY [às] HH:mm")}</span></div>
                        </div>
                    ))}
                    {!campaign?.history?.length && <p>Os próximos eventos desta campanha serão registrados aqui.</p>}
                </div>
            </section>

            <section className={styles.recipientsSection}>
                <div className={styles.recipientsHeader}>
                    <div>
                        <span className={styles.eyebrow}>Destinatários</span>
                        <h2>Acompanhamento dos envios</h2>
                        <p>{total} registro{total === 1 ? "" : "s"}{hasActiveFilters ? " encontrado(s) para os filtros aplicados" : " na campanha"}</p>
                    </div>
                    {isLoading && campaign && <span className={styles.filterLoading}>Atualizando resultados…</span>}
                </div>
                <div className={styles.filters}>
                    <div className={styles.searchControl}>
                        <Input
                            type="search"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            placeholder="Buscar por contato ou telefone"
                            aria-label="Buscar destinatário por contato ou telefone"
                        />
                    </div>
                    <div className={styles.statusControl}>
                        <Select
                            name="Todos os status"
                            placeholder="Buscar status"
                            options={optionsStatus}
                            selected={statusFilter}
                            onChange={value => setStatusFilter(value || "")}
                        />
                    </div>
                    {hasActiveFilters && (
                        <Button type="button" variant="secondary" fullWidth={false} onClick={clearFilters}>
                            <MdFilterAltOff /> Limpar filtros
                        </Button>
                    )}
                </div>
                <div className={styles.tables}>
                    <Table
                        headers={headers}
                        values={getTableValues()}
                        onScroll={handleScroll}
                        loadMore={isFetchingMore}
                        showIndex
                        messageNotRegisters={hasActiveFilters ? "Nenhum destinatário encontrado para estes filtros." : "Nenhum envio encontrado."}
                    />
                </div>
            </section>
            {retryConfirmOpen && (
                <CampaignRetryModal
                    eligible={effectiveRetryEligible}
                    onSubmit={handleRetryFailures}
                    closeModal={() => setRetryConfirmOpen(false)}
                />
            )}
            {scheduleModal && <CampaignScheduleModal count={1} onSubmit={scheduled_at => handleCampaignAction("reschedule", { scheduled_at })} closeModal={() => setScheduleModal(false)} />}
        </div>
    )
}

export default OutboundCampaignDetails
